import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import {
  usersApi,
  UserResponse,
  CreateUserData,
  UpdateUserData,
} from "../lib/users";
import { sitesApi } from "../lib/sites";
import { groupsApi, GroupResponse } from "../lib/groups";
import { organisationsApi } from "../lib/organisations";
import { useAuth } from "../hooks/useAuth";
import { Save, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { canManageUsers } from "../utils/role-utils";

const NONE_ORGANISATION = "__none_org__";

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const isEditing = id !== "new";
  const isSiteAdmin = currentUser?.role === "site_admin";
  const isSuperUser = currentUser?.role === "super_user";
  const restrictToEndUsers = isSiteAdmin;
  const isEditingSelf = isEditing && id === currentUser?.id;

  const [formData, setFormData] = useState<{
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    role: "super_user" | "site_admin" | "end_user" | undefined;
    organisationId: string | null;
    siteIds: string[];
    groupIds: string[];
  }>({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    role: undefined,
    organisationId: null,
    siteIds: [],
    groupIds: [],
  });

  const [passwordError, setPasswordError] = useState<string>("");
  const [sitesGroupsError, setSitesGroupsError] = useState<string>("");
  const [formError, setFormError] = useState<string>("");
  const [forceResetMessage, setForceResetMessage] = useState<string>("");
  const [forceResetError, setForceResetError] = useState<string>("");

  const effectiveRole: "super_user" | "site_admin" | "end_user" | undefined =
    restrictToEndUsers ? "end_user" : formData.role;
  const isEndUserRole = effectiveRole === "end_user";
  const isSiteAdminRole = effectiveRole === "site_admin";

  // Fetch user data if editing (but not if editing self - use currentUser instead)
  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ["user", id],
    queryFn: () => usersApi.getById(id!),
    enabled: isEditing && !!id && !isEditingSelf,
  });

  const { data: sites } = useQuery({
    queryKey: ["sites"],
    queryFn: () => sitesApi.getAll(),
    enabled: !isEditingSelf, // Don't fetch if editing self
  });

  const { data: allGroups } = useQuery({
    queryKey: ["groups"],
    queryFn: () => groupsApi.getAll(),
    enabled: !isEditingSelf, // Don't fetch if editing self
  });

  const { data: organisations, isLoading: organisationsLoading } = useQuery({
    queryKey: ["organisations"],
    queryFn: () => organisationsApi.getAll(),
    enabled: isSuperUser && !isEditingSelf,
  });

  // Organize groups by site for hierarchical display
  const groupsBySite =
    allGroups?.reduce((acc, group) => {
      const siteId = group.siteId;
      if (!acc[siteId]) {
        acc[siteId] = [];
      }
      acc[siteId].push(group);
      return acc;
    }, {} as Record<string, GroupResponse[]>) || {};

  /** Effective organisation used to scope available sites (form selection or site admin's org). */
  const scopeOrganisationId = isSiteAdmin
    ? currentUser?.organisationId ?? null
    : formData.organisationId;

  /** Sites available for assignment; scoped to the selected / site-admin organisation when set. */
  const sortedSites = useMemo(() => {
    const list = sites ?? [];
    const filtered = scopeOrganisationId
      ? list.filter((site) => site.organisationId === scopeOrganisationId)
      : list;
    return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  }, [sites, scopeOrganisationId]);

  /** Site admins may only open users that belong to their organisation. */
  const isOutsideSiteAdminOrganisation =
    isSiteAdmin &&
    !isEditingSelf &&
    isEditing &&
    !!currentUser?.organisationId &&
    !!user &&
    user.organisationId !== currentUser.organisationId;

  // Load user data when editing
  useEffect(() => {
    if (user) {
      const userRole = restrictToEndUsers ? "end_user" : user.role;
      setFormData({
        email: user.email,
        password: "",
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        role: userRole,
        organisationId: isSiteAdmin
          ? currentUser?.organisationId ?? user.organisationId ?? null
          : user.organisationId ?? null,
        siteIds: user.sites?.map((s) => s.id) || [],
        // Only load groups if the user is an end user
        groupIds:
          userRole === "end_user" ? user.groups?.map((g) => g.id) || [] : [],
      });
    } else if (!isEditing) {
      // Reset form for new user. Site admins can only create end users.
      setFormData({
        email: "",
        password: "",
        firstName: "",
        lastName: "",
        role: restrictToEndUsers ? "end_user" : undefined,
        organisationId: isSiteAdmin
          ? currentUser?.organisationId ?? null
          : null,
        siteIds: [],
        groupIds: [],
      });
    } else if (isEditingSelf && currentUser) {
      // Load current user data for self-editing
      setFormData({
        email: currentUser.email,
        password: "",
        firstName: currentUser.firstName || "",
        lastName: currentUser.lastName || "",
        role: currentUser.role,
        organisationId: currentUser.organisationId ?? null,
        siteIds: [],
        groupIds: [],
      });
    }
  }, [
    user,
    restrictToEndUsers,
    isEditing,
    isEditingSelf,
    isSiteAdmin,
    currentUser,
  ]);

  const handleOrganisationChange = (value: string) => {
    const nextOrganisationId = value === NONE_ORGANISATION ? null : value;

    setSitesGroupsError("");
    setFormData((prev) => {
      if (prev.organisationId === nextOrganisationId) {
        return prev;
      }

      // Keep only sites (and their groups) that belong to the newly selected organisation.
      // When clearing organisation, keep all current selections.
      if (!nextOrganisationId) {
        return { ...prev, organisationId: null };
      }

      const allowedSiteIds = new Set(
        (sites ?? [])
          .filter((site) => site.organisationId === nextOrganisationId)
          .map((site) => site.id),
      );
      const nextSiteIds = prev.siteIds.filter((siteId) =>
        allowedSiteIds.has(siteId),
      );
      const removedSiteIds = prev.siteIds.filter(
        (siteId) => !allowedSiteIds.has(siteId),
      );
      const removedGroupIds = new Set(
        removedSiteIds.flatMap((siteId) =>
          (groupsBySite[siteId] || []).map((g) => g.id),
        ),
      );
      const nextGroupIds = prev.groupIds.filter(
        (groupId) => !removedGroupIds.has(groupId),
      );

      return {
        ...prev,
        organisationId: nextOrganisationId,
        siteIds: nextSiteIds,
        groupIds: nextGroupIds,
      };
    });
  };

  const handleSiteToggle = (siteId: string) => {
    setSitesGroupsError("");
    setFormData((prev) => {
      const isSelected = prev.siteIds.includes(siteId);
      const newSiteIds = isSelected
        ? prev.siteIds.filter((id) => id !== siteId)
        : [...prev.siteIds, siteId];

      // If deselecting a site, also deselect all its groups
      const siteGroups = groupsBySite[siteId] || [];
      const newGroupIds = isSelected
        ? prev.groupIds.filter((id) => !siteGroups.some((g) => g.id === id))
        : prev.groupIds;

      return {
        ...prev,
        siteIds: newSiteIds,
        groupIds: newGroupIds,
      };
    });
  };

  const handleGroupToggle = (groupId: string, siteId: string) => {
    // Only allow group toggling for end_user role
    if (formData.role !== "end_user" && !restrictToEndUsers) {
      return;
    }

    setSitesGroupsError("");
    setFormData((prev) => {
      const isSelected = prev.groupIds.includes(groupId);
      const newGroupIds = isSelected
        ? prev.groupIds.filter((id) => id !== groupId)
        : [...prev.groupIds, groupId];

      // If selecting a group, automatically select its parent site
      const newSiteIds = isSelected
        ? prev.siteIds // Keep sites as is when deselecting
        : prev.siteIds.includes(siteId)
        ? prev.siteIds // Site already selected
        : [...prev.siteIds, siteId]; // Add site if not already selected

      return {
        ...prev,
        siteIds: newSiteIds,
        groupIds: newGroupIds,
      };
    });
  };

  const createMutation = useMutation({
    mutationFn: (data: CreateUserData) => usersApi.create(data),
    onSuccess: (response: UserResponse) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      navigate("/users", {
        state: {
          notice: response.warning
            ? {
                message: response.warning,
                variant: "destructive" as const,
              }
            : {
                message:
                  "User created. An invite email has been sent so they can set their password.",
                variant: "default" as const,
              },
        },
      });
    },
    onError: (error: unknown) => {
      console.error("Error creating user:", error);
      let errorMessage = "Failed to create user";
      if (error && typeof error === "object") {
        const err = error as { response?: { data?: { error?: string } }, message?: string };
        errorMessage =
          err?.response?.data?.error ||
          err?.message ||
          "Failed to create user";
      }
      setFormError(errorMessage);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserData }) =>
      usersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["user", id] });
      queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
      navigate(isEditingSelf ? "/" : "/users");
    },
    onError: (error: unknown) => {
      console.error("Error updating user:", error);
      let errorMessage = "Failed to update user";
      if (error && typeof error === "object") {
        const err = error as { response?: { data?: { error?: string } }, message?: string };
        errorMessage =
          err?.response?.data?.error ||
          err?.message ||
        "Failed to update user";
      }
      setFormError(errorMessage);
    },
  });

  const forceResetMutation = useMutation({
    mutationFn: (userId: string) => usersApi.forcePasswordReset(userId),
    onSuccess: (response) => {
      setForceResetError("");
      setForceResetMessage(response.message || "Password reset email sent");
    },
    onError: (error: unknown) => {
      setForceResetMessage("");
      const err = error as { response?: { data?: { error?: string } }; message?: string };
      setForceResetError(
        err?.response?.data?.error || err?.message || "Failed to send password reset email",
      );
    },
  });

  // Check permissions - allow if user can manage users OR if editing self
  const hasPermission = canManageUsers(currentUser?.role) || isEditingSelf;
  if (!hasPermission) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>
            You do not have permission to access this page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const validatePassword = (password: string): string => {
    if (password && password.length < 8) {
      return "Password must be at least 8 characters long";
    }
    return "";
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");

    // Optional password only applies when editing (self or another user)
    if (isEditing) {
      const passwordValidationError = validatePassword(formData.password);
      if (passwordValidationError) {
        setPasswordError(passwordValidationError);
        return;
      }
    }
    setPasswordError("");

    // For self-editing, only allow firstName, lastName, and password
    if (isEditingSelf) {
      const submitData: UpdateUserData = {
        firstName: formData.firstName || undefined,
        lastName: formData.lastName || undefined,
      };

      if (formData.password && formData.password.trim().length > 0) {
        submitData.password = formData.password;
      }

      updateMutation.mutate({ id: id!, data: submitData });
      return;
    }

    // Validate role is selected
    if (!restrictToEndUsers && !formData.role) {
      setFormError("Please select a role");
      return;
    }

    // Validate that site_admin and end_user roles have at least one site.
    // Use effectiveRole so site-admin creators (role forced to end_user in the UI)
    // still include site/group allocations even when formData.role is unset.
    if (
      (effectiveRole === "site_admin" || effectiveRole === "end_user") &&
      formData.siteIds.length === 0
    ) {
      setSitesGroupsError(
        `${
          effectiveRole === "site_admin" ? "Site admin" : "End user"
        } roles must be allocated to at least one site`
      );
      return;
    }

    // End users need at least one group in each selected site (groups grant area access).
    if (effectiveRole === "end_user") {
      const sitesMissingGroups = formData.siteIds
        .map((siteId) => {
          const site = sortedSites.find((s) => s.id === siteId);
          const siteGroups = groupsBySite[siteId] || [];
          const hasGroup = siteGroups.some((g) =>
            formData.groupIds.includes(g.id)
          );
          return hasGroup ? null : site?.name ?? siteId;
        })
        .filter((name): name is string => name !== null);

      if (sitesMissingGroups.length > 0) {
        setSitesGroupsError(
          `End users must be allocated to at least one group in each selected site. Missing groups for: ${sitesMissingGroups.join(
            ", "
          )}.`
        );
        return;
      }
    }
    setSitesGroupsError("");

    const submitData: CreateUserData | UpdateUserData = {
      email: formData.email,
      firstName: formData.firstName || undefined,
      lastName: formData.lastName || undefined,
      role: effectiveRole,
      organisationId: isSuperUser
        ? formData.organisationId
        : isSiteAdmin
          ? currentUser?.organisationId ?? null
          : undefined,
      siteIds:
        (effectiveRole === "site_admin" || effectiveRole === "end_user") &&
        formData.siteIds.length > 0
          ? formData.siteIds
          : undefined,
      groupIds:
        effectiveRole === "end_user" && formData.groupIds.length > 0
          ? formData.groupIds
          : undefined,
    };

    if (isEditing && formData.password && formData.password.trim().length > 0) {
      (submitData as UpdateUserData).password = formData.password;
    }

    if (isEditing && id) {
      updateMutation.mutate({ id, data: submitData as UpdateUserData });
    } else {
      createMutation.mutate(submitData as CreateUserData);
    }
  };

  if (userLoading && !isEditingSelf) {
    return <div className="p-6">Loading...</div>;
  }

  if (isOutsideSiteAdminOrganisation) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>
            You can only manage users in your organisation.
          </AlertDescription>
        </Alert>
        <Button
          variant="ghost"
          onClick={() => navigate("/users")}
          className="mt-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Users
        </Button>
      </div>
    );
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="px-2 py-3 sm:px-0 max-w-4xl mx-auto">
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/users")}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Users
        </Button>
        <h1 className="text-2xl sm:text-3xl font-bold font-heading">
          {isEditingSelf
            ? "Edit My Profile"
            : isEditing
            ? "Edit User"
            : "Create User"}
        </h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {isEditingSelf
              ? "Edit My Profile"
              : isEditing
              ? "Edit User"
              : "Create User"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {formError && (
              <Alert variant="destructive">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}

            {isEditingSelf ? (
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  disabled
                  className="bg-muted"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              </div>
            )}

            {!isEditing && (
              <Alert>
                <AlertDescription>
                  An invite email will be sent to this address so the user can set
                  their own password.
                </AlertDescription>
              </Alert>
            )}

            {isEditing && (
              <div className="space-y-2">
                <Label htmlFor="password">
                  Password (leave blank to keep current)
                </Label>
                <Input
                  id="password"
                  type="password"
                  minLength={formData.password.length > 0 ? 8 : undefined}
                  value={formData.password}
                  onChange={(e) => {
                    setFormData({ ...formData, password: e.target.value });
                    if (passwordError) {
                      setPasswordError("");
                    }
                  }}
                  className={passwordError ? "border-destructive" : ""}
                />
                {passwordError && (
                  <p className="text-xs text-destructive">{passwordError}</p>
                )}
                {!passwordError && formData.password.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Password must be at least 8 characters
                  </p>
                )}
                {!isEditingSelf && canManageUsers(currentUser?.role) && (
                  <div className="pt-2 space-y-2">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={forceResetMutation.isPending || !id}
                      onClick={() => {
                        setForceResetMessage("");
                        setForceResetError("");
                        if (id) {
                          forceResetMutation.mutate(id);
                        }
                      }}
                    >
                      {forceResetMutation.isPending
                        ? "Sending..."
                        : "Force password reset"}
                    </Button>
                    {forceResetMessage && (
                      <p className="text-xs text-muted-foreground">
                        {forceResetMessage}
                      </p>
                    )}
                    {forceResetError && (
                      <p className="text-xs text-destructive">{forceResetError}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {isEditingSelf && (
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Input
                  id="role"
                  type="text"
                  value={formData.role || ""}
                  disabled
                  className="bg-muted"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={(e) =>
                  setFormData({ ...formData, firstName: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                type="text"
                value={formData.lastName}
                onChange={(e) =>
                  setFormData({ ...formData, lastName: e.target.value })
                }
              />
            </div>

            {!isEditingSelf && (
              <>
                {isSuperUser && (
                  <div className="space-y-2">
                    <Label htmlFor="organisation">Organisation</Label>
                    <Select
                      value={(formData.organisationId ?? NONE_ORGANISATION) as string}
                      onValueChange={handleOrganisationChange}
                      disabled={organisationsLoading || isSubmitting}
                    >
                      <SelectTrigger id="organisation">
                        <SelectValue placeholder="Choose organisation…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE_ORGANISATION}>None</SelectItem>
                        {(organisations ?? []).map((org) => (
                          <SelectItem key={org.id} value={org.id}>
                            {org.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Optional. When set, only sites in this organisation can be assigned,
                      and organisation branding is shown on the user&apos;s dashboard.
                    </p>
                  </div>
                )}

                {!restrictToEndUsers && (
                  <div className="space-y-2">
                    <Label htmlFor="role">Role *</Label>
                    <Select
                      value={formData.role || undefined}
                      onValueChange={(value) => {
                        const newRole = value as
                          | "super_user"
                          | "site_admin"
                          | "end_user";
                        setSitesGroupsError("");
                        setFormError("");
                        setFormData({
                          ...formData,
                          role: newRole,
                          // Clear groups if role is not end_user
                          groupIds:
                            newRole === "end_user" ? formData.groupIds : [],
                          // Keep sites for site_admin and end_user
                          siteIds: formData.siteIds,
                        });
                      }}
                    >
                      <SelectTrigger id="role">
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="end_user">End User</SelectItem>
                        <SelectItem value="site_admin">Site Admin</SelectItem>
                        <SelectItem value="super_user">Super User</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {restrictToEndUsers && (
                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Input
                      id="role"
                      type="text"
                      value="End User"
                      disabled
                      className="bg-muted"
                    />
                  </div>
                )}

                <div className="space-y-2">
                {isSiteAdminRole && (
                    <Label>
                      Sites
                    </Label>
                  )}
                  {isEndUserRole && (
                    <Label>
                      Sites & Groups
                    </Label>
                  )}
                  {!restrictToEndUsers && !isEndUserRole && !isSiteAdminRole ? (
                    <Card className="max-h-96 overflow-y-auto p-2 bg-muted opacity-60">
                      <p className="text-sm text-muted-foreground py-2">
                        Groups are only available for end users. Please select
                        "End User" as the role to assign groups.
                      </p>
                    </Card>
                  ) : (
                    <Card className="max-h-96 overflow-y-auto p-2">
                      {sortedSites.length > 0 ? (
                        isSiteAdminRole ? (
                          // Simple list for site admin role (no accordion)
                          <div className="space-y-2">
                            {sortedSites.map((site) => {
                              const isSiteSelected = formData.siteIds.includes(
                                site.id
                              );
                              return (
                                <label
                                  key={site.id}
                                  className="flex items-center space-x-2 py-2 px-2 hover:bg-accent rounded cursor-pointer"
                                >
                                  <Checkbox
                                    checked={isSiteSelected}
                                    onCheckedChange={() =>
                                      handleSiteToggle(site.id)
                                    }
                                    className="mt-0.5"
                                  />
                                  <div className="flex-1">
                                    <p className="text-sm font-medium">
                                      {site.name}
                                    </p>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        ) : (
                          // Accordion for end user role (with groups)
                          <Accordion type="multiple" className="w-full">
                            {sortedSites.map((site) => {
                              const siteGroups = groupsBySite[site.id] || [];
                              const isSiteSelected = formData.siteIds.includes(
                                site.id
                              );
                              const selectedGroupCount = siteGroups.filter((g) =>
                                formData.groupIds.includes(g.id)
                              ).length;

                              return (
                                <AccordionItem key={site.id} value={site.id}>
                                  <div className="flex items-center space-x-2 border-b">
                                    <Checkbox
                                      checked={isSiteSelected}
                                      onCheckedChange={() =>
                                        handleSiteToggle(site.id)
                                      }
                                      className="ml-4"
                                    />
                                    <AccordionTrigger className="flex-1 text-sm hover:no-underline">
                                      <div className="flex items-center w-full pr-2 gap-2">
                                        <span className="font-medium flex-1 min-w-0 truncate">
                                          {site.name}
                                        </span>
                                        {siteGroups.length > 0 &&
                                          isEndUserRole && (
                                            <span className="text-xs text-muted-foreground text-right min-w-fit">
                                              {selectedGroupCount > 0 && (
                                                <span className="mr-2">
                                                  {selectedGroupCount} of{" "}
                                                  {siteGroups.length} groups
                                                </span>
                                              )}
                                              {siteGroups.length} group
                                              {siteGroups.length !== 1 ? "s" : ""}
                                            </span>
                                          )}
                                      </div>
                                    </AccordionTrigger>
                                  </div>
                                  {isEndUserRole && (
                                    <AccordionContent>
                                      {siteGroups.length > 0 ? (
                                        <div className="space-y-2 pt-2 ml-4">
                                          {siteGroups.map((group) => {
                                            const isGroupSelectable = isEndUserRole;
                                            return (
                                              <label
                                                key={group.id}
                                                className={`flex items-start space-x-2 py-1 px-2 rounded ${
                                                  isGroupSelectable
                                                    ? "hover:bg-accent cursor-pointer"
                                                    : "opacity-60 cursor-not-allowed"
                                                }`}
                                              >
                                                <Checkbox
                                                  checked={formData.groupIds.includes(
                                                    group.id
                                                  )}
                                                  onCheckedChange={() =>
                                                    isGroupSelectable &&
                                                    handleGroupToggle(
                                                      group.id,
                                                      site.id
                                                    )
                                                  }
                                                  disabled={!isGroupSelectable}
                                                  className="mt-0.5"
                                                />
                                                <div className="flex-1">
                                                  <p className="text-sm">
                                                    {group.name}
                                                  </p>
                                                  {group.pin && (
                                                    <p className="text-xs text-muted-foreground">
                                                      PIN: {group.pin}
                                                    </p>
                                                  )}
                                                  {!isGroupSelectable && (
                                                    <p className="text-xs text-muted-foreground italic">
                                                      Groups are only available for
                                                      end users
                                                    </p>
                                                  )}
                                                </div>
                                              </label>
                                            );
                                          })}
                                        </div>
                                      ) : (
                                        <p className="text-sm text-muted-foreground py-2 ml-4">
                                          No groups in this site
                                        </p>
                                      )}
                                    </AccordionContent>
                                  )}
                                </AccordionItem>
                              );
                            })}
                          </Accordion>
                        )
                      ) : (
                        <p className="text-sm text-muted-foreground py-2">
                          {scopeOrganisationId
                            ? "No sites in this organisation"
                            : "No sites available"}
                        </p>
                      )}
                    </Card>
                  )}
                  {(formData.siteIds.length > 0 ||
                    formData.groupIds.length > 0) && (
                    <div className="flex gap-4 text-xs text-muted-foreground">
                      {formData.siteIds.length > 0 && (
                        <span>
                          {formData.siteIds.length} site
                          {formData.siteIds.length !== 1 ? "s" : ""} selected
                        </span>
                      )}
                      {formData.groupIds.length > 0 && (
                        <span>
                          {formData.groupIds.length} group
                          {formData.groupIds.length !== 1 ? "s" : ""} selected
                        </span>
                      )}
                    </div>
                  )}
                  {sitesGroupsError && (
                    <Alert variant="destructive">
                      <AlertDescription>{sitesGroupsError}</AlertDescription>
                    </Alert>
                  )}
                </div>
              </>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(isEditingSelf ? "/" : "/users")}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                <Save className="h-4 w-4 mr-2" />
                {isSubmitting ? "Saving..." : isEditing ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
